import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Building2, DollarSign, TrendingUp, Server, Search, Edit, Check, X, Handshake, ChevronDown, ChevronRight, Activity, Boxes, Wifi, WifiOff, Clock, RefreshCw } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import ActiveSessionsPanel from '../components/ActiveSessionsPanel';
import LoginHistoryPanel from '../components/LoginHistoryPanel';

import { parseUtc } from '../lib/parseUtc';

// Compact "how long ago" for fleet timestamps. Backend sends UTC instants, some
// without an offset, so every value goes through parseUtc first.
function timeAgo(value) {
  if (!value) return 'never';
  const t = parseUtc(value);
  if (isNaN(t.getTime())) return 'unknown';
  const mins = (Date.now() - t.getTime()) / 60000;
  if (mins < 1) return 'just now';
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

function fullTime(value) {
  if (!value) return 'never';
  const t = parseUtc(value);
  return isNaN(t.getTime()) ? 'unknown' : t.toLocaleString();
}

// Status vocabulary mirrors the backend's thresholds exactly (live <=30m,
// stale <=24h, then disconnected; never_reported = onboarded but no data yet).
const FLEET_STATUS = {
  live: { label: 'Live', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  stale: { label: 'Stale', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  disconnected: { label: 'Disconnected', cls: 'bg-red-100 text-red-700 border-red-200' },
  never_reported: { label: 'Never reported', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};

function FleetStat({ icon: Icon, label, value, sub, tone = 'text-gray-900' }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-600">{label}</p>
          <Icon className="w-4 h-4 text-gray-400" />
        </div>
        <div className={`text-3xl font-bold ${tone}`}>{value}</div>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function SuperAdmin() {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [partnerCustomers, setPartnerCustomers] = useState({});
  const [editingCommission, setEditingCommission] = useState(null);
  const [commissionValue, setCommissionValue] = useState('');
  const [fleet, setFleet] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(true);

  useEffect(() => {
    fetchSuperAdminData();
  }, []);

  const fetchSuperAdminData = async () => {
    try {
      const [clientsRes, statsRes, partnersRes, fleetRes] = await Promise.all([
        axios.get(`${API}/superadmin/clients`),
        axios.get(`${API}/superadmin/stats`),
        axios.get(`${API}/superadmin/partners`).catch(() => ({ data: { partners: [] } })),
        axios.get(`${API}/superadmin/fleet-overview`).catch((e) => {
          console.error('fleet-overview failed:', e);
          return { data: null };
        })
      ]);
      setClients(clientsRes.data.clients || []);
      setStats(statsRes.data);
      setPartners(partnersRes.data.partners || []);
      setFleet(fleetRes.data);
      setFleetLoading(false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching superadmin data:', error);
      toast.error('Failed to fetch data');
      setLoading(false);
    }
  };

  const updateClientPlan = async (clientId, newPlan) => {
    try {
      await axios.put(`${API}/superadmin/clients/${clientId}/plan`, { plan: newPlan });
      toast.success('Plan updated successfully');
      fetchSuperAdminData();
      setEditingClient(null);
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const togglePartner = async (partner) => {
    const code = partner.referral_code;
    if (expandedPartner === code) {
      setExpandedPartner(null);
      return;
    }
    setExpandedPartner(code);
    if (!partnerCustomers[code]) {
      try {
        const res = await axios.get(`${API}/superadmin/partners/${code}/customers`);
        setPartnerCustomers(prev => ({ ...prev, [code]: res.data }));
      } catch {
        toast.error('Failed to fetch partner customers');
        setPartnerCustomers(prev => ({ ...prev, [code]: { customers: [] } }));
      }
    }
  };

  const updateCommission = async (referralCode, customerId, value) => {
    try {
      await axios.put(`${API}/superadmin/partners/${referralCode}/customers/${customerId}/commission`, { commission_percentage: parseFloat(value) });
      toast.success('Commission updated');
      const res = await axios.get(`${API}/superadmin/partners/${referralCode}/customers`);
      setPartnerCustomers(prev => ({ ...prev, [referralCode]: res.data }));
      setEditingCommission(null);
    } catch {
      toast.error('Failed to update commission');
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-8">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              SuperAdmin Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Manage clients, plans, and track platform metrics</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm opacity-90 font-medium">Total Clients</p>
              <Users className="w-10 h-10 opacity-30" />
            </div>
            <div className="text-4xl font-bold">{stats?.total_clients || 0}</div>
            <p className="text-xs opacity-75 mt-1">{stats?.active_clients || 0} active</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm opacity-90 font-medium">Total Clusters</p>
              <Server className="w-10 h-10 opacity-30" />
            </div>
            <div className="text-4xl font-bold">{stats?.total_clusters || 0}</div>
            <p className="text-xs opacity-75 mt-1">Across all clients</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm opacity-90 font-medium">Realized Savings (all clusters)</p>
              <DollarSign className="w-10 h-10 opacity-30" />
            </div>
            <div className="text-4xl font-bold">${((stats?.total_realized_savings ?? stats?.total_savings) || 0).toFixed(0)}</div>
            <p className="text-xs opacity-75 mt-1">Platform-wide</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm opacity-90 font-medium">Monthly Revenue</p>
              <TrendingUp className="w-10 h-10 opacity-30" />
            </div>
            <div className="text-4xl font-bold">${(stats?.monthly_revenue || 0).toFixed(0)}</div>
            <p className="text-xs opacity-75 mt-1">Recurring</p>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="fleet" className="w-full">
          {/* 7 triggers after the merge: upstream's Fleet tab plus the six
              existing ones — grid-cols must match or the row squashes. */}
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="fleet">Fleet</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="partners">Partners</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="sessions">Active Sessions</TabsTrigger>
            <TabsTrigger value="logins">Login History</TabsTrigger>
          </TabsList>

          <TabsContent value="fleet" className="space-y-4">
            {fleetLoading ? (
              <Card><CardContent className="pt-6 text-gray-500">Loading fleet…</CardContent></Card>
            ) : !fleet ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-red-600 font-medium">Could not load fleet overview.</p>
                  <button
                    onClick={fetchSuperAdminData}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Retry
                  </button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Headline numbers. Savings here are the SAME two figures the
                    customer dashboard shows: month-to-date realized, and the
                    lifetime cumulative total. */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <FleetStat
                    icon={Wifi}
                    label="Clusters live"
                    value={fleet.totals.clusters_live}
                    sub={`of ${fleet.totals.clusters_registered} registered`}
                    tone="text-emerald-600"
                  />
                  <FleetStat
                    icon={WifiOff}
                    label="Not reporting"
                    value={fleet.totals.clusters_stale + fleet.totals.clusters_disconnected + fleet.totals.clusters_never_reported}
                    sub={`${fleet.totals.clusters_stale} stale · ${fleet.totals.clusters_disconnected} dead · ${fleet.totals.clusters_never_reported} never`}
                    tone="text-amber-600"
                  />
                  <FleetStat icon={Server} label="Nodes" value={fleet.totals.nodes_live} sub="live clusters only" />
                  <FleetStat icon={Boxes} label="Pods" value={fleet.totals.pods_live} sub="live clusters only" />
                  <FleetStat
                    icon={DollarSign}
                    label="Realized (MTD)"
                    value={`$${(fleet.totals.realized_savings_mtd || 0).toLocaleString()}`}
                    sub="month to date"
                    tone="text-emerald-600"
                  />
                  <FleetStat
                    icon={TrendingUp}
                    label="Saved till now"
                    value={`$${(fleet.totals.saved_till_now || 0).toLocaleString()}`}
                    sub="lifetime cumulative"
                    tone="text-emerald-600"
                  />
                </div>

                {/* Platform activity */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-gray-600">Last accessed</p>
                        <Activity className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="text-2xl font-bold">{timeAgo(fleet.activity.last_accessed_at)}</div>
                      <p className="text-xs text-gray-500 mt-1">
                        {fleet.activity.last_accessed_email || 'no activity recorded yet'}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{fullTime(fleet.activity.last_accessed_at)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-gray-600">Last login</p>
                        <Users className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="text-2xl font-bold">{timeAgo(fleet.activity.last_login_at)}</div>
                      <p className="text-xs text-gray-500 mt-1">
                        {fleet.activity.last_login_email || 'no login recorded yet'}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{fullTime(fleet.activity.last_login_at)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-gray-600">Last data reported</p>
                        <Clock className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="text-2xl font-bold">{timeAgo(fleet.activity.last_data_reported_at)}</div>
                      <p className="text-xs text-gray-500 mt-1">
                        {fleet.totals.accounts_active_24h} account(s) active in 24h
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{fullTime(fleet.activity.last_data_reported_at)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Per-cluster detail */}
                <Card className="shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 flex flex-row items-center justify-between">
                    <CardTitle className="text-xl">Clusters</CardTitle>
                    <button
                      onClick={fetchSuperAdminData}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 font-semibold">Cluster</th>
                            <th className="text-left p-3 font-semibold">Owner</th>
                            <th className="text-left p-3 font-semibold">Status</th>
                            <th className="text-left p-3 font-semibold">Last data</th>
                            <th className="text-right p-3 font-semibold">Nodes</th>
                            <th className="text-right p-3 font-semibold">Pods</th>
                            <th className="text-right p-3 font-semibold">MTD saved</th>
                            <th className="text-right p-3 font-semibold">Till now</th>
                            <th className="text-right p-3 font-semibold">Run rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fleet.clusters.map((c) => {
                            const st = FLEET_STATUS[c.status] || FLEET_STATUS.never_reported;
                            return (
                              <tr key={c.cluster_id} className="border-b hover:bg-gray-50">
                                <td className="p-3">
                                  <p className="font-medium">{c.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {[c.provider, c.region, c.connection_mode].filter(Boolean).join(' · ') || '—'}
                                  </p>
                                </td>
                                <td className="p-3">
                                  <p className="text-gray-700">{c.owner_email || '—'}</p>
                                  <p className="text-xs text-gray-500">
                                    accessed {timeAgo(c.owner_last_accessed)}
                                  </p>
                                </td>
                                <td className="p-3">
                                  <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${st.cls}`}>
                                    {st.label}
                                  </span>
                                </td>
                                <td className="p-3" title={fullTime(c.last_reported_at)}>
                                  {timeAgo(c.last_reported_at)}
                                </td>
                                <td className="p-3 text-right">{c.nodes}</td>
                                <td className="p-3 text-right">{c.pods}</td>
                                <td className="p-3 text-right font-medium text-emerald-600">
                                  ${(c.realized_savings_mtd || 0).toLocaleString()}
                                </td>
                                <td className="p-3 text-right font-medium text-emerald-700">
                                  ${(c.saved_till_now || 0).toLocaleString()}
                                </td>
                                <td className="p-3 text-right text-gray-600">
                                  ${(c.monthly_cost_run_rate || 0).toLocaleString()}/mo
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {fleet.clusters.length === 0 && (
                        <p className="text-gray-500 py-6 text-center">No clusters registered.</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-4">
                      Live = reported within {fleet.thresholds.live_within_minutes}m · stale = within{' '}
                      {Math.round(fleet.thresholds.stale_within_minutes / 60)}h · node and pod totals count live
                      clusters only. Generated {fullTime(fleet.generated_at)}.
                    </p>
                  </CardContent>
                </Card>

                {/* Who has been using the platform */}
                <Card>
                  <CardHeader><CardTitle className="text-lg">Recent platform activity</CardTitle></CardHeader>
                  <CardContent>
                    {(fleet.activity.recent_activity || []).length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No activity recorded yet. Access tracking starts from the next sign-in.
                      </p>
                    ) : (
                      <div className="divide-y">
                        {fleet.activity.recent_activity.map((u) => (
                          <div key={u.email} className="flex items-center justify-between py-2">
                            <div>
                              <p className="font-medium text-sm">{u.full_name || u.email}</p>
                              <p className="text-xs text-gray-500">{u.email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm">{timeAgo(u.last_accessed)}</p>
                              <p className="text-xs text-gray-500">login {timeAgo(u.last_login)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-3">
                      {fleet.totals.accounts_with_clusters} of {fleet.totals.accounts_total} accounts own a cluster ·{' '}
                      {fleet.totals.accounts_never_accessed} never accessed.
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="clients" className="space-y-4">
            {/* Search */}
            <Card>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Clients Table */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                <CardTitle className="text-xl">Client Management</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold">Client</th>
                        <th className="text-left p-3 font-semibold">Plan</th>
                        <th className="text-left p-3 font-semibold">Clusters</th>
                        <th className="text-left p-3 font-semibold">Realized Savings</th>
                        <th className="text-left p-3 font-semibold">Status</th>
                        <th className="text-left p-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map((client) => (
                        <tr key={client.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{client.name}</p>
                              <p className="text-sm text-gray-600">{client.email}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            {editingClient === client.id ? (
                              <div className="flex items-center gap-2">
                                <select
                                  defaultValue={client.plan}
                                  onChange={(e) => updateClientPlan(client.id, e.target.value)}
                                  className="border rounded px-2 py-1 text-sm"
                                >
                                  <option value="free">Free</option>
                                  <option value="starter">Starter</option>
                                  <option value="professional">Professional</option>
                                  <option value="enterprise">Enterprise</option>
                                </select>
                                <button onClick={() => setEditingClient(null)} className="text-gray-500">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  client.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                                  client.plan === 'professional' ? 'bg-blue-100 text-blue-800' :
                                  client.plan === 'starter' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }>
                                  {client.plan}
                                </Badge>
                                <button onClick={() => setEditingClient(client.id)} className="text-blue-600">
                                  <Edit className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="font-medium">{client.clusters_count || 0}</span>
                          </td>
                          <td className="p-3">
                            <span className="font-medium text-green-600">
                              ${((client.total_realized_savings ?? client.total_savings) || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="p-3">
                            <Badge className={client.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {client.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <button className="text-blue-600 hover:underline text-sm">
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="partners" className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Handshake className="w-5 h-5" />
                  Partner Management
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {partners.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No partners found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-semibold w-8"></th>
                          <th className="text-left p-3 font-semibold">Name</th>
                          <th className="text-left p-3 font-semibold">Email</th>
                          <th className="text-left p-3 font-semibold">Company</th>
                          <th className="text-left p-3 font-semibold">Referral Code</th>
                          <th className="text-left p-3 font-semibold">Customers</th>
                          <th className="text-left p-3 font-semibold">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partners.map((p) => (
                          <React.Fragment key={p.id}>
                            <tr
                              className="border-b hover:bg-gray-50 cursor-pointer"
                              onClick={() => togglePartner(p)}
                            >
                              <td className="p-3">
                                {expandedPartner === p.referral_code
                                  ? <ChevronDown className="w-4 h-4 text-gray-500" />
                                  : <ChevronRight className="w-4 h-4 text-gray-500" />}
                              </td>
                              <td className="p-3 font-medium">{p.full_name}</td>
                              <td className="p-3 text-gray-600">{p.email}</td>
                              <td className="p-3">{p.company_name}</td>
                              <td className="p-3">
                                <Badge className="bg-blue-100 text-blue-800 font-mono">{p.referral_code}</Badge>
                              </td>
                              <td className="p-3 font-medium">{p.total_customers || 0}</td>
                              <td className="p-3 text-gray-600">{parseUtc(p.created_at).toLocaleDateString()}</td>
                            </tr>
                            {expandedPartner === p.referral_code && (
                              <tr>
                                <td colSpan="7" className="p-0">
                                  <div className="bg-gray-50 p-4 border-b">
                                    {(() => {
                                      const data = partnerCustomers[p.referral_code];
                                      const custs = data?.customers || [];
                                      if (!data) return <p className="text-sm text-gray-500">Loading...</p>;
                                      if (custs.length === 0) return <p className="text-sm text-gray-500">No customers yet</p>;
                                      return (
                                        <>
                                          <div className="flex gap-4 mb-4">
                                            <div className="bg-white rounded-lg px-4 py-2 border shadow-sm">
                                              <p className="text-xs text-gray-500">Customers</p>
                                              <p className="text-lg font-bold">{data.count}</p>
                                            </div>
                                            <div className="bg-white rounded-lg px-4 py-2 border shadow-sm">
                                              <p className="text-xs text-gray-500">Total Savings</p>
                                              <p className="text-lg font-bold text-blue-600">${data.total_savings}/mo</p>
                                            </div>
                                            <div className="bg-white rounded-lg px-4 py-2 border shadow-sm">
                                              <p className="text-xs text-gray-500">Commission ({(data.commission_rate * 100)}%)</p>
                                              <p className="text-lg font-bold text-green-600">${data.total_commission}/mo</p>
                                            </div>
                                          </div>
                                          <table className="w-full text-sm">
                                            <thead>
                                              <tr className="border-b bg-white">
                                                <th className="text-left p-2 font-semibold">Name</th>
                                                <th className="text-left p-2 font-semibold">Email</th>
                                                <th className="text-left p-2 font-semibold">Plan</th>
                                                <th className="text-left p-2 font-semibold">Monthly Savings</th>
                                                <th className="text-left p-2 font-semibold">Commission %</th>
                                                <th className="text-left p-2 font-semibold">Commission</th>
                                                <th className="text-left p-2 font-semibold">Clusters</th>
                                                <th className="text-left p-2 font-semibold">Savings</th>
                                                <th className="text-left p-2 font-semibold">Joined</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {custs.map((c) => (
                                                <tr key={c.id || c.email} className="border-b bg-white hover:bg-blue-50">
                                                  <td className="p-2">{c.full_name}</td>
                                                  <td className="p-2 text-gray-600">{c.email}</td>
                                                  <td className="p-2">
                                                    <Badge className={
                                                      c.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                                                      c.plan === 'professional' ? 'bg-blue-100 text-blue-800' :
                                                      c.plan === 'starter' ? 'bg-green-100 text-green-800' :
                                                      'bg-gray-100 text-gray-800'
                                                    }>{c.plan}</Badge>
                                                  </td>
                                                  <td className="p-2 font-medium">${(c.monthly_savings || 0).toFixed(2)}</td>
                                                  <td className="p-2 font-medium text-green-600">
                                                    {editingCommission === c.id ? (
                                                      <div className="flex items-center gap-1">
                                                        <input
                                                          type="number"
                                                          step="0.1"
                                                          value={commissionValue}
                                                          onChange={(e) => setCommissionValue(e.target.value)}
                                                          className="w-16 border rounded px-1 py-0.5 text-sm text-black"
                                                          onKeyDown={(e) => e.key === 'Enter' && updateCommission(p.referral_code, c.id, commissionValue)}
                                                        />%
                                                        <button onClick={() => updateCommission(p.referral_code, c.id, commissionValue)} className="text-green-600"><Check className="w-3 h-3" /></button>
                                                        <button onClick={() => setEditingCommission(null)} className="text-gray-500"><X className="w-3 h-3" /></button>
                                                      </div>
                                                    ) : (
                                                      <span className="flex items-center gap-1">
                                                        {c.commission_percentage}%
                                                        <button onClick={(e) => { e.stopPropagation(); setEditingCommission(c.id); setCommissionValue(c.commission_percentage); }} className="text-blue-500"><Edit className="w-3 h-3" /></button>
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td className="p-2 font-medium text-green-600">${c.commission}</td>
                                                  <td className="p-2">{c.clusters_count || 0}</td>
                                                  <td className="p-2 text-green-600">${(c.total_savings || 0).toFixed(2)}</td>
                                                  <td className="p-2 text-gray-600">{c.created_at ? parseUtc(c.created_at).toLocaleDateString() : '-'}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Clients by Plan */}
              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                  <CardTitle className="text-xl">Clients by Plan</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats?.clients_by_plan || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={100}
                        dataKey="count"
                      >
                        {(stats?.clients_by_plan || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Savings by Client */}
              <Card className="shadow-lg">
                <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
                  <CardTitle className="text-xl">Top Clients by Savings</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats?.top_clients_by_savings || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="savings" fill="#10b981" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Growth Trend */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="text-xl">Platform Growth</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats?.growth_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip />
                    <Line type="monotone" dataKey="clients" stroke="#3b82f6" strokeWidth={2} name="Clients" />
                    <Line type="monotone" dataKey="clusters" stroke="#10b981" strokeWidth={2} name="Clusters" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="revenue" className="space-y-6">
            {/* Revenue Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="shadow-lg border-l-4 border-blue-500">
                <CardHeader>
                  <CardTitle className="text-sm">Monthly Recurring Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    ${(stats?.monthly_revenue || 0).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">+12% from last month</p>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-l-4 border-green-500">
                <CardHeader>
                  <CardTitle className="text-sm">Annual Run Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    ${((stats?.monthly_revenue || 0) * 12).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Projected annually</p>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-l-4 border-purple-500">
                <CardHeader>
                  <CardTitle className="text-sm">Average Revenue Per Client</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">
                    ${((stats?.monthly_revenue || 0) / (stats?.total_clients || 1)).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Per month</p>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Breakdown */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
                <CardTitle className="text-xl">Revenue by Plan</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats?.revenue_by_plan || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="plan" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="revenue" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="space-y-4">
            <ActiveSessionsPanel />
          </TabsContent>

          <TabsContent value="logins" className="space-y-4">
            <LoginHistoryPanel />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default SuperAdmin;
